import { lazy, Suspense, useRef, useState } from "react";
import { useLanguage } from "@/lib/LanguageContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Calendar arrastra react-day-picker + date-fns; se carga en su propio
// chunk sólo cuando el usuario abre el selector de fecha.
const Calendar = lazy(() =>
    import("@/components/ui/calendar").then((m) => ({ default: m.Calendar })),
);
import { toast } from "sonner";
import Logo3D from "@/components/Logo3D";
import { successBurst } from "@/three/successBurst";

function encodeForm(data) {
    return Object.keys(data)
        .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(data[k])}`)
        .join("&");
}

export default function Contact() {
    const { t } = useLanguage();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [budget, setBudget] = useState("");
    const [description, setDescription] = useState("");
    const [date, setDate] = useState(undefined);
    const [status, setStatus] = useState("idle"); // idle | sending | sent | error
    const submitRef = useRef(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        try {
            const res = await fetch("/", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: encodeForm({
                    "form-name": "contact",
                    name,
                    email,
                    budget,
                    description,
                    preferred_date: date ? date.toDateString() : "",
                }),
            });
            if (!res.ok) throw new Error("Network error");
            setStatus("sent");
            toast.success(t.contact.form.success);
            successBurst(submitRef.current);
            setName(""); setEmail(""); setBudget(""); setDescription(""); setDate(undefined);
            setTimeout(() => setStatus("idle"), 4000);
        } catch (err) {
            setStatus("error");
            toast.error(t.contact.form.error);
        }
    };

    return (
        <section id="contact" className="ps-contact ps-relative-z" data-testid="contact-section">
            <div className="ps-container">
                <div className="ps-contact-top">
                    <div className="ps-contact-intro ps-fade">
                        <span className="ps-availability-badge" data-testid="contact-badge">
                            <span className="pulse" />
                            {t.contact.badge}
                        </span>
                        <h2 className="ps-contact-title" data-split data-split-scroll data-testid="contact-title"
                            dangerouslySetInnerHTML={{ __html: t.contact.title }} />
                        <p className="ps-contact-desc">{t.contact.desc}</p>
                        <div className="ps-contact-cta-row">
                            <a href="mailto:pernaletestudio@gmail.com" className="ps-btn ps-btn-primary" data-testid="contact-mail">
                                <span>{t.contact.ctaEmail}</span>
                                <span className="ps-btn-dot" />
                            </a>
                            <a href="https://wa.me/15642257839" target="_blank" rel="noopener noreferrer" className="ps-btn" data-testid="social-whatsapp">
                                <span>{t.contact.ctaWhatsapp}</span>
                                <span className="ps-btn-dot" />
                            </a>
                        </div>
                    </div>

                    <form
                        name="contact"
                        onSubmit={handleSubmit}
                        className="ps-contact-form ps-fade"
                        data-testid="contact-form"
                    >
                        <input type="hidden" name="form-name" value="contact" />
                        <p hidden>
                            <label>Don't fill this out: <input name="bot-field" /></label>
                        </p>

                        <input
                            type="text" name="name" placeholder={t.contact.form.name}
                            value={name} onChange={(e) => setName(e.target.value)} required
                            data-testid="contact-input-name"
                        />
                        <div className="ps-contact-form-row">
                            <input
                                type="email" name="email" placeholder={t.contact.form.email}
                                value={email} onChange={(e) => setEmail(e.target.value)} required
                                data-testid="contact-input-email"
                            />
                            <input
                                type="text" name="budget" placeholder={t.contact.form.budget}
                                value={budget} onChange={(e) => setBudget(e.target.value)}
                                data-testid="contact-input-budget"
                            />
                        </div>
                        <Popover>
                            <PopoverTrigger asChild>
                                <button type="button" className="ps-contact-date-btn" data-testid="contact-date-trigger">
                                    {date ? date.toLocaleDateString() : t.contact.form.pickDate}
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                                <Suspense fallback={<div className="ps-cal-loading" style={{ padding: "1rem", fontSize: "0.85rem", opacity: 0.7 }}>…</div>}>
                                    <Calendar mode="single" selected={date} onSelect={setDate} initialFocus />
                                </Suspense>
                            </PopoverContent>
                        </Popover>
                        <textarea
                            name="description" placeholder={t.contact.form.description}
                            value={description} onChange={(e) => setDescription(e.target.value)} required
                            rows={4} data-testid="contact-input-description"
                        />
                        <button ref={submitRef} type="submit" className="ps-btn ps-btn-primary ps-contact-submit" disabled={status === "sending" || status === "sent"} data-testid="contact-submit">
                            <span>{status === "sending" ? t.contact.form.sending : status === "sent" ? t.contact.form.sentLabel : t.contact.form.submit}</span>
                            <span className="ps-btn-dot" />
                        </button>
                    </form>
                </div>

                <div className="ps-footer" data-testid="footer">
                    <a href="#hero" className="ps-footer-logo">
                        <Logo3D size={24} />
                        <span>Pernalete<em>°</em> Studio</span>
                    </a>
                    <div>{t.contact.footer}</div>
                    <div className="ps-footer-links">
                        <a href="mailto:pernaletestudio@gmail.com" className="ps-footer-pill" data-testid="footer-mail">{t.contact.channels.newProjects.h}</a>
                        <a href="https://wa.me/15642257839" target="_blank" rel="noopener noreferrer" className="ps-footer-pill">{t.contact.channels.whatsapp.h}</a>
                    </div>
                </div>
            </div>
        </section>
    );
}
